import type {
  IndividualInvestorWizardValues,
  CompanyInvestorWizardValues,
} from "@/domain/investorWizard";
import type {
  CreateCompanyInvestorRequest,
  CreateIndividualInvestorRequest,
} from "@/domain/investor";
import { splitRepresentativeName } from "@/domain/representative";
import type { InvestorWizardFormData } from "@/domain/validation/investorSchemas";

function buildCommonCreateFields(data: InvestorWizardFormData) {
  return {
    email: data.email?.trim() || undefined,
    phone: data.phone?.trim() || undefined,
    address: data.fiscalAddress?.trim() || undefined,
    city: data.city?.trim() || undefined,
    postal_code: data.postalcode?.trim() || undefined,
    country: data.country || undefined,
    communication_language: data.language || "es",
    taxable_income_base: data.taxableIncomeBase || undefined,
    potential_investment_amount: data.potentialInvestmentAmount || undefined,
    cultural_interests: data.investmentInterests || [],
  };
}

export const toCreateIndividualInvestorRequest = (
  data: IndividualInvestorWizardValues,
): CreateIndividualInvestorRequest => ({
  first_name: data.firstName.trim(),
  last_name: data.lastName.trim(),
  national_id: data.dni.trim(),
  birth_date: data.birthDate || undefined,
  profession: data.profession?.trim() || undefined,
  ...buildCommonCreateFields(data),
});

export const toCreateCompanyInvestorRequest = (
  data: CompanyInvestorWizardValues,
): CreateCompanyInvestorRequest => {
  const { firstName, lastName } = splitRepresentativeName(data.representative.fullName);
  const representatives = [
    {
      first_name: firstName,
      last_name: lastName,
      dni: data.representative.dni.trim(),
      position: data.representative.position || null,
      phone: data.representative.phone.trim() || null,
      email: data.representative.email.trim() || null,
      is_primary: true,
      is_signer: true,
    },
  ];
  const primary = representatives[0];

  return {
    company_name: data.companyName.trim(),
    tax_id: data.cif.trim(),
    legal_rep_first_name: primary?.first_name || undefined,
    legal_rep_last_name: primary?.last_name || undefined,
    legal_rep_national_id: primary?.dni || undefined,
    website: data.website?.trim() || undefined,
    signature_regime: data.signatureRegime ?? "sole",
    legal_representatives: representatives,
    ...buildCommonCreateFields(data),
  };
};
